import WalletButton from 'features/cardManagement/components/WalletButton/WalletButton';
import {
  WalletAnalyticsContext,
  WalletResponseType,
} from 'features/cardManagement/components/WalletButton/WalletButtonShared';
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Illustration, {
  IllustrationSourceType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';

export interface WalletOnboardingCardProps {
  /** Text to describe the purpose of the card */
  bodyText: string;
  /** Text that describe what action clicking on the card triggers */
  illustration?: IllustrationSourceType;
  /** Boolean to disable the card */
  isDisabled?: boolean;
  cardId: string;
  primaryAccountNumberSuffix: string;
  cardholderName: string;
  isDebit: boolean;
  /** Action to be perfomed when the card is clicked */
  onPress?: () => void;
  /** callback to be performed when flow completes returning result type */
  onFlowComplete?: (response: WalletResponseType) => void;
}

export const WalletOnboardingCard = ({
  bodyText,
  illustration,
  isDisabled = false,
  cardId,
  primaryAccountNumberSuffix,
  cardholderName,
  isDebit,
  onPress,
  onFlowComplete,
}: WalletOnboardingCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [manualWalletTrigger, setManualWalletTrigger] = useState<boolean>();

  const flowCompletion = (response: WalletResponseType) => {
    onFlowComplete?.(response);
    setManualWalletTrigger(false);
  };

  const analytics: WalletAnalyticsContext = {
    sourceName: 'onboarding_cards',
    category: 'onboarding',
    subCategory: 'onboarding_nurture',
  };
  return (
    <Pressable
      style={[
        styles.cardContainer,
        isFocused && styles.focus,
        isHovered && styles.hover,
        isPressed && styles.press,
        isDisabled && styles.disabled,
      ]}
      onHoverIn={() => {
        setIsHovered(true);
      }}
      onHoverOut={() => {
        setIsHovered(false);
      }}
      onBlur={() => {
        setIsFocused(false);
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onPressIn={() => {
        setIsPressed(true);
      }}
      onPressOut={() => {
        setIsPressed(false);
      }}
      onPress={() => {
        onPress?.();
        setManualWalletTrigger(true);
      }}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      role="button"
    >
      <View style={styles.contentContainer}>
        <View style={styles.textContainer}>
          <BodyBaseText
            style={isDisabled && styles.disabledText}
            numberOfLines={3}
          >
            {bodyText}
          </BodyBaseText>
        </View>
        <WalletButton
          cardId={cardId}
          maskedCardNumber={primaryAccountNumberSuffix}
          cardholderName={cardholderName}
          isDebit={isDebit}
          manuallyTriggerFlow={manualWalletTrigger}
          enableProvisioningOnTap={false}
          style={styles.button}
          onFlowComplete={flowCompletion}
          analyticsContextOverride={analytics}
          sentryPageName="Wallet NBA Onboarding Card"
        />
      </View>
      <View style={styles.illustration}>
        <Illustration illustration={illustration ?? 'PLACEHOLDER_A'} />
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    height: 42,
  },
  cardContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.secondary,
    borderColor: theme.color.background.secondary,
    borderRadius: 4,
    borderWidth: 1,
    flexBasis: 'auto',
    flexDirection: 'row',
    flex: 1,
    minHeight: 126,
    overflow: 'hidden',
  },
  contentContainer: {
    flex: 1,
    gap: theme.spacing.s,
    justifyContent: 'space-between',
    margin: theme.spacing.m,
  },
  disabled: {
    backgroundColor: theme.color.background.secondary,
  },
  disabledText: {
    color: theme.color.text.muted,
  },
  focus: {
    backgroundColor: theme.color.status.infoPressed,
    borderColor: theme.color.border.focused,
  },
  hover: {
    backgroundColor: theme.color.status.infoLightPressed,
  },
  illustration: {
    height: 70,
    marginRight: theme.spacing.s,
    minWidth: 0,
    overflow: 'hidden',
    width: 79,
  },
  press: {
    backgroundColor: theme.color.status.infoPressed,
  },
  textContainer: {
    flex: 1,
  },
});

export default WalletOnboardingCard;
